<template>
    <div class='main'>
        <div class='content'>
            <div class='content-header'>
                <h5>データダウンロード</h5>
                <p class='note'>「データ販売」サイズで注文した写真をダウンロードできます。</p>
            </div>
            <p v-if='loading' class='message'>読み込み中...</p>
            <p v-else-if='!orders.length' class='message'>ダウンロード可能な写真はありません。</p>
            <div v-else class='order-list'>
                <div v-for='order in orders' :key='order.orderId' class='order-card'>
                    <div class='order-header'>
                        <div class='order-meta'>
                            <span class='order-date'>{{ formatDate(order.createdAt) }}</span>
                            <span class='order-number'>注文番号: {{ order.orderNumber }}</span>
                            <span class='order-count'>{{ order.photos.length }}枚</span>
                        </div>
                        <button class='button is-info is-small' :disabled='isBulkDownloading(order)' @click='downloadAll(order)'>
                            <i class='fas fa-file-archive'></i>&nbsp;<span>{{ isBulkDownloading(order) ? `準備中… ${bulkProgress(order)}` : '一括ダウンロード' }}</span>
                        </button>
                    </div>
                    <div class='photos'>
                        <div v-for='photo in order.photos' :key='photo.id' class='photo'>
                            <div class='thumb-wrap'>
                                <img v-if='photo.reductionS3Key' :src='`${photoBaseUrl}/${photo.reductionS3Key}`' loading='lazy' decoding='async'>
                                <img v-else-if='photo.s3Key' :src='`${photoBaseUrl}/${photo.s3Key}`' loading='lazy' decoding='async'>
                                <div v-else class='no-thumb'>画像なし</div>
                            </div>
                            <p class='photo-name'>{{ photo.name }}</p>
                            <button class='button is-small is-light' :disabled='isDownloading(order, photo)' @click='download(order, photo)'>
                                <i class='fas fa-download'></i>&nbsp;<span>{{ isDownloading(order, photo) ? 'DL中…' : 'DL' }}</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import fileDownload from 'js-file-download'
import JSZip from 'jszip'

export default {
    name: 'Downloads',
    data () {
        return {
            photoBaseUrl: process.env.PHOTO_BASE_URL,
            orders: [],
            loading: true,
            downloadingMap: {},
            bulkMap: {}
        }
    },
    mounted () {
        this.fetchDownloads()
    },
    methods: {
        fetchDownloads () {
            this.loading = true
            this.axios.get(`${process.env.API_URL}/photobuy/downloads`).then((res) => {
                this.orders = res.data.orders || []
            }).catch(() => {
                this.$root.$emit('notification', 'is-danger', '一覧の取得に失敗しました')
            }).finally(() => {
                this.loading = false
            })
        },
        photoKey (order, photo) {
            return `${order.orderId}-${photo.id}`
        },
        isDownloading (order, photo) {
            return !!this.downloadingMap[this.photoKey(order, photo)]
        },
        isBulkDownloading (order) {
            return !!this.bulkMap[order.orderId]
        },
        bulkProgress (order) {
            const p = this.bulkMap[order.orderId]
            if (!p) return ''
            return `${p.done}/${p.total}`
        },
        fetchBlob (order, photo) {
            return this.axios.get(`${process.env.API_URL}/photobuy/download/${order.orderId}/${photo.id}`, {
                responseType: 'blob'
            }).then((res) => res.data)
        },
        download (order, photo) {
            const key = this.photoKey(order, photo)
            this.$set(this.downloadingMap, key, true)
            this.fetchBlob(order, photo).then((blob) => {
                fileDownload(blob, photo.name || `photo_${photo.id}.jpg`)
            }).catch(() => {
                this.$root.$emit('notification', 'is-danger', 'ダウンロードに失敗しました')
            }).finally(() => {
                this.$delete(this.downloadingMap, key)
            })
        },
        async downloadAll (order) {
            if (this.isBulkDownloading(order)) return
            this.$set(this.bulkMap, order.orderId, { done: 0, total: order.photos.length })
            const zip = new JSZip()
            const usedNames = {}
            let failed = 0

            for (const photo of order.photos) {
                try {
                    const blob = await this.fetchBlob(order, photo)
                    let name = photo.name || `photo_${photo.id}.jpg`
                    if (usedNames[name]) {
                        const dot = name.lastIndexOf('.')
                        const base = dot > 0 ? name.slice(0, dot) : name
                        const ext = dot > 0 ? name.slice(dot) : ''
                        name = `${base}_${usedNames[name]}${ext}`
                    }
                    usedNames[photo.name || name] = (usedNames[photo.name || name] || 0) + 1
                    zip.file(name, blob)
                } catch (e) {
                    failed++
                }
                const p = this.bulkMap[order.orderId]
                if (p) this.$set(this.bulkMap, order.orderId, { done: p.done + 1, total: p.total })
            }

            try {
                const zipBlob = await zip.generateAsync({ type: 'blob' })
                const zipName = `${order.orderNumber || 'order_' + order.orderId}.zip`
                fileDownload(zipBlob, zipName)
                if (failed > 0) {
                    this.$root.$emit('notification', 'is-danger', `${failed}件のダウンロードに失敗しました`)
                }
            } catch (e) {
                this.$root.$emit('notification', 'is-danger', 'ZIPの作成に失敗しました')
            } finally {
                this.$delete(this.bulkMap, order.orderId)
            }
        },
        formatDate (x) {
            if (!x) return ''
            const date = new Date(x)
            const y = date.getFullYear()
            const m = ('0' + (date.getMonth() + 1)).slice(-2)
            const d = ('0' + date.getDate()).slice(-2)
            return `${y}-${m}-${d}`
        }
    }
}
</script>

<style scoped>
.content {
    padding: 25px;
    margin: auto;
    min-width: 0;
}

.content-header {
    margin-bottom: 16px;
}

.note {
    margin-top: 6px;
    color: #666;
    font-size: 13px;
}

.message {
    margin-top: 24px;
    color: #555;
}

.order-list {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.order-card {
    border: 1px solid #e2e5ea;
    border-radius: 8px;
    background: #fff;
    overflow: hidden;
}

.order-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 12px 16px;
    background: #f8f9fb;
    border-bottom: 1px solid #e2e5ea;
    flex-wrap: wrap;
}

.order-meta {
    display: flex;
    align-items: center;
    gap: 14px;
    flex-wrap: wrap;
    font-size: 13px;
    color: #444;
}

.order-date {
    font-weight: 600;
    color: #222;
}

.order-number {
    color: #666;
}

.order-count {
    background: #eef1ff;
    color: #3b3f8f;
    padding: 2px 8px;
    border-radius: 999px;
    font-weight: 600;
    font-size: 12px;
}

.photos {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 12px;
    padding: 14px;
}

.photo {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 6px;
    border: 1px solid #eee;
    border-radius: 6px;
    padding: 8px;
    background: #fff;
}

.thumb-wrap {
    aspect-ratio: 1 / 1;
    background: #f0f0f0;
    border-radius: 4px;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
}

.thumb-wrap > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}

.no-thumb {
    color: #999;
    font-size: 12px;
}

.photo-name {
    font-size: 12px;
    color: #333;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

@media (max-width: 600px) {
    .photos {
        grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
        padding: 10px;
    }

    .order-meta {
        gap: 10px;
        font-size: 12px;
    }
}
</style>



// WEBPACK FOOTER //
// src/components/photobuy/pages/Downloads.vue